import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, absFile, evDate } from "@/lib/api";
import { themeFor } from "@/lib/themes";
import { EventDecor, Ticket } from "@/components/Motifs";
import { countWord } from "@/lib/GuestContext";

export default function Celebrations() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/public/events").then((r) => setEvents(r.data || [])); }, []);
  return (
    <div data-testid="celebrations-page">
      <section className="bg-[#FFF9F0] grain">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="kicker text-[#FF6657] rise">{events.length === 1 ? "The celebration" : "Our celebrations"}</div>
          <h1 className="font-display text-6xl md:text-[8rem] leading-[0.85] tracking-[-0.04em] mt-4 rise d1" data-testid="celebrations-title">
            {events.length === 1 ? <>Your night<br /><span className="italic text-[#A62D58]">with us.</span></> : <>{countWord(events.length)} parties.<br /><span className="italic text-[#A62D58]">{countWord(events.length)} moods.</span></>}
          </h1>
          <p className="font-hand text-2xl md:text-3xl mt-6 max-w-2xl rise d2">{events.length === 1 ? "Everything you need, all in one place." : "Pack accordingly. Hydrate. Stretch. You've been warned."}</p>
          <div className="flex flex-wrap gap-3 mt-10 rise d3">
            {events.map((e) => {
              const t = themeFor(e.slug);
              return <a key={e.slug} href={`#${e.slug}`} className="btn-pop rounded-full px-5 py-2.5 kicker" style={{ background: t.bg, color: t.fg }}>{e.name}</a>;
            })}
          </div>
        </div>
      </section>
      {events.map((e, i) => <Chapter key={e.slug} e={e} i={i} />)}
    </div>
  );
}

function Chapter({ e, i }) {
  const t = themeFor(e.slug);
  const d = evDate(e.date).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "long" });
  const flip = i % 2 === 1;
  return (
    <section id={e.slug} data-testid={`celebration-${e.slug}`} className="relative overflow-hidden" style={{ background: t.bg, color: t.fg }}>
      <EventDecor slug={e.slug} t={t} page />
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}>
          <div className={`overflow-hidden h-[380px] md:h-[540px] ${t.imgClass} ${e.slug === "sangeet" ? "aspect-square h-auto max-w-[460px] mx-auto" : ""}`}>
            <img src={absFile(e.image_url || t.image)} alt={e.name} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
          </div>
        </div>
        <div className="md:col-span-6 min-w-0">
          <div className="kicker" style={{ color: t.accent }}>No. 0{i + 1} · {t.label}</div>
          <h2 className={`${e.slug === "reception" ? "text-4xl sm:text-5xl md:text-6xl lg:text-7xl" : "text-5xl sm:text-6xl md:text-8xl"} leading-[0.9] mt-4 break-words ${t.titleClass}`}>{e.name}</h2>
          <p className="font-display italic text-2xl md:text-3xl mt-6" style={{ color: e.slug === "wedding" ? t.accent : t.accent }}>{e.headline || t.headline}</p>
          <p className="font-hand text-xl md:text-2xl mt-3 opacity-90">{e.tagline || t.tagline}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Ticket notch={t.bg} style={{ background: t.fg, color: t.bg }}>{d}</Ticket>
            <Ticket notch={t.bg} style={{ background: t.accent, color: e.slug === "reception" ? t.bg : "#FFF9F0" }}>{e.venue}</Ticket>
          </div>
          <Link to={`/celebrations/${e.slug}`} data-testid={`event-link-${e.slug}`} className="btn-pop mt-10 inline-flex items-center gap-2 rounded-full px-7 py-4 kicker bg-[#FFF9F0] text-[#252122]">
            Step inside <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
